import React, { useEffect, useRef } from 'react';
import { getAssetPath } from '../utils/assetHelper';
import './Modal.scss';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  isLargeWidth?: boolean;
  pattern?: boolean;
  isNotification?: boolean;
  notificationType?: 'success' | 'warning' | 'error' | 'info';
  hideHeader?: boolean;
  hideCloseButton?: boolean;
  className?: string;
  icon?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  isLargeWidth = false,
  pattern = false,
  isNotification = false,
  notificationType = 'success',
  hideHeader = false,
  hideCloseButton = false,
  className = '',
  icon,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key press to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Handle overlay click to close modal
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="c-modal-wrapper" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div 
        className="c-modal-overlay" 
        onClick={handleOverlayClick}
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0, 0, 0, 0.7)', 
          backdropFilter: 'blur(5px)',
          zIndex: 1
        }}
      ></div>
      <div 
        className={`c-modal__container ${isLargeWidth ? 'c-modal__container--large' : ''} ${pattern ? 'c-modal__container--pattern' : ''} ${isNotification ? `c-modal__container--notification c-modal__container--notification-${notificationType}` : ''} ${className}`}
        ref={modalRef}
        style={{ 
          position: 'relative', 
          zIndex: 2, 
          backgroundColor: '#1E2A38', 
          borderRadius: '20px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
          maxWidth: isLargeWidth ? '800px' : '500px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'hidden'
        }}
      >
        {!hideCloseButton && (
          <button 
            className="c-modal__close" 
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              cursor: 'pointer',
              zIndex: 3
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 1L1 13" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M1 1L13 13" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        
        {!hideHeader && title && (
          <div 
            className="c-modal__header"
            style={{
              padding: '20px 25px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <h1 style={{ 
              margin: 0, 
              color: '#FFFFFF', 
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {icon && <span className="modal-icon" style={{ marginRight: '10px' }}>{icon}</span>}
              {title}
            </h1>
          </div>
        )}
        
        <div 
          className="c-modal__content"
          style={{
            padding: '130px',
            maxHeight: 'calc(90vh - 80px)',
            overflowY: 'auto'
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal; 